import {
  just as structuredJust,
  Maybe as StructuredMaybe,
  nothing as structuredNothing,
} from '@execonline-inc/structured-maybe';
import { toUrlT } from '@execonline-inc/url';
import { assertNever } from '@kofno/piper';
import { succeed } from 'jsonous';
import { just, Maybe, nothing } from 'maybeasy';
import { action, computed, makeObservable, observable } from 'mobx';
import { Task } from 'taskarian';
import { callApi } from '../../../../../../Appy';
import { currentUserStore } from '../../../../../../CurrentUserStore';
import { findLinkBy } from '../../../../../../Links';
import { findLinkT } from '../../../../../../LinkyLinky';
import { Link } from '../../../../../../Resource/Types';
import { toWallClockString } from '../../../../../../Utils/timezone';
import { SelectableUserGroup } from '../../../../../Form/GroupInputWithSuggestions/Types';
import { shareUrlResourceDecoder } from '../../../../Decoders';
import { ShareUrlResource } from '../../../../Types';
import {
  learningCollectionResourceDecoder,
  participantsResponseDecoder,
  shareLearningCollectionResponseDecoder,
} from './Decoders';
import {
  confirming,
  error,
  FailedParticipant,
  GroupParticipant,
  isActiveShareState,
  LearningCollection,
  LearningCollectionResource,
  LicenseOption,
  loading,
  noShareUrl,
  Participant,
  Participants,
  ParticipantStatus,
  publishing,
  ready,
  refreshing,
  ReviewEntry,
  reviewing,
  searching,
  ShareBy,
  ShareLearningCollectionError,
  ShareLearningCollectionResponse,
  ShareState,
  ShareType,
  shareUrlError,
  shareUrlLoading,
  shareUrlReady,
  ShareUrlState,
  submitting,
  SuccessfulParticipant,
  UnlicensedAccessOption,
  UnverifiedParticipant,
  UnlicensedUserParticipant,
  VerifiedUserParticipant,
  waiting,
} from './Types';

export class ShareStore {
  @observable state: ShareState = waiting();
  @observable private currentCollectionId: Maybe<number> = nothing();

  constructor() {
    makeObservable(this);
  }

  @action
  load = (
    learningCollectionId: number,
    shareBy: ShareBy,
    shareType: ShareType,
    programId: Maybe<number>,
  ): void => {
    // Check if we've already loaded this collection
    const shouldReload = this.currentCollectionId.cata({
      Just: (id) => {
        if (id !== learningCollectionId) {
          return true; // Different collection - reload
        }

        // Same collection - check if we should reload based on state
        switch (this.state.kind) {
          case 'waiting':
          case 'error':
          case 'loading':
            return true; // Need to load
          case 'ready':
          case 'searching':
          case 'reviewing':
          case 'refreshing':
          case 'publishing':
          case 'submitting':
          case 'confirming':
            // Already loaded - update params if needed
            const programIdChanged = this.state.programId.cata({
              Just: (currentId) =>
                programId.cata({
                  Just: (newId) => currentId !== newId,
                  Nothing: () => true,
                }),
              Nothing: () => programId.isJust(),
            });
            if (
              this.state.shareBy !== shareBy ||
              this.state.shareType !== shareType ||
              programIdChanged
            ) {
              this.state.shareBy = shareBy;
              this.state.shareType = shareType;
              this.state.programId = programId;
            }
            return false; // Don't reload
        }
      },
      Nothing: () => true, // No collection loaded yet - reload
    });

    if (!shouldReload) {
      return;
    }

    // New collection or error state - reload
    this.currentCollectionId = just(learningCollectionId);
    this.state = loading(learningCollectionId, shareBy, shareType, programId);

    this.loadLearningCollectionTask(learningCollectionId).fork(
      action((err: ShareLearningCollectionError) => {
        console.error('Failed to load learning collection:', err);
        this.state = error('An error occurred, please refresh the page and try again');
      }),
      action((learningCollectionResource: LearningCollectionResource) => {
        this.state = ready(learningCollectionResource, shareBy, shareType, programId);
      }),
    );
  };

  @action
  togglePublishAndReload = (
    publish: boolean,
    onSuccess?: (publishedAt: StructuredMaybe<Date>) => void,
  ): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state = publishing(this.state);

        this.currentCollectionId.do((collectionId) => {
          this.togglePublishLearningCollectionTask(publish).fork(
            action((err: ShareLearningCollectionError) => {
              console.error('Failed to toggle publish learning collection:', err);
              this.state = error('An error occurred, please refresh the page and try again');
            }),
            action(() => {
              // Toggle succeeded - now reload to get fresh data
              if (this.state.kind === 'publishing') {
                this.loadLearningCollectionTask(collectionId).fork(
                  action((err: ShareLearningCollectionError) => {
                    console.error(
                      'Failed to reload learning collection after toggling publish:',
                      err,
                    );
                    this.state = error('An error occurred, please refresh the page and try again');
                  }),
                  action((learningCollectionResource: LearningCollectionResource) => {
                    if (this.state.kind === 'publishing') {
                      this.state = {
                        ...this.state,
                        kind: 'ready',
                        learningCollectionResource,
                      };
                      // Notify the callback with the updated publishedAt value
                      if (onSuccess) {
                        // Convert from maybeasy Maybe to StructuredMaybe
                        const structuredPublishedAt: StructuredMaybe<Date> =
                          learningCollectionResource.payload.publishedAt.cata<
                            StructuredMaybe<Date>
                          >({
                            Just: (date) => structuredJust(date),
                            Nothing: () => structuredNothing(),
                          });
                        onSuccess(structuredPublishedAt);
                      }
                    }
                  }),
                );
              }
            }),
          );
        });
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateUserGroup = (group: SelectableUserGroup): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.userGroup = group;
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  toggleScheduleEnabled = (): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.scheduleEnabled = !this.state.scheduleEnabled;
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateScheduleDate = (date: Date): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.scheduleDate = date;
        this.state.scheduleModified = true;
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateScheduleTime = (time: Date): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.scheduleTime = time;
        this.state.scheduleModified = true;
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateScheduleTimezone = (timezone: string): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state.scheduleTimezone = timezone;
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  addParticipants = (inputText: string): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing': {
        const parsedParticipants = this.parseParticipantInput(inputText);

        // Filter out duplicates
        const newParticipants = parsedParticipants.filter((participant) => {
          if (participant.kind === 'user') {
            return !this.existingEmails.has(participant.email.toLowerCase());
          } else {
            return !this.existingGroupNames.has(participant.name.toLowerCase());
          }
        });

        this.state.participants = [...this.state.participants, ...newParticipants];

        // Trigger verification for newly added participants
        if (newParticipants.length > 0) {
          setTimeout(
            action(() => this.verifyParticipants()),
            100,
          );
        }
        break;
      }
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  removeParticipant = (participantToRemove: Participant): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.participants = this.state.participants.filter(
          (participant) => !this.participantEquals(participant, participantToRemove),
        );
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateParticipantStatus = (participantToUpdate: Participant, status: ParticipantStatus): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.participants = this.state.participants.map((participant) => {
          if (!this.participantEquals(participant, participantToUpdate)) {
            return participant;
          }

          // Return participant with updated status
          return { ...participant, status } as Participant;
        });
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  clearParticipants = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.participants = [];
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  verifyParticipants = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing': {
        const pendingParticipants = this.state.participants.filter((p) => p.status === 'pending');
        if (pendingParticipants.length === 0) return;

        const emails = pendingParticipants.filter((p) => p.kind === 'user').map((p) => p.email);
        const groupNames = pendingParticipants.filter((p) => p.kind === 'group').map((p) => p.name);
        // The collection decides which licenses count: one starting after its
        // last course ends gives nothing here (DVLP-8068).
        const learningCollectionId = this.state.learningCollectionResource.payload.id;

        this.verifyParticipantsTask(emails, groupNames, learningCollectionId).fork(
          action((error: ShareLearningCollectionError) => {
            console.error('Failed to verify participants:', error);
          }),
          action((verifiedParticipants: Participant[]) => {
            this.updateVerifiedParticipants(verifiedParticipants);
          }),
        );
        break;
      }
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateVerifiedParticipants = (verifiedParticipants: Participant[]): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.participants = this.state.participants.map((participant) => {
          // Only process participants that were in pending state
          if (participant.status !== 'pending') {
            return participant;
          }

          const verified = verifiedParticipants.find((vp) => {
            if (participant.kind === 'user' && vp.kind === 'user') {
              return participant.email === vp.email;
            }
            if (participant.kind === 'group' && vp.kind === 'group') {
              return participant.name === vp.name;
            }
            return false;
          });

          if (verified) {
            // Replace unverified participant with response from server
            return verified;
          }

          // If participant was pending but not in the response, mark as not-found
          if (participant.kind === 'user') {
            return {
              kind: 'user',
              email: participant.email,
              name: participant.email,
              status: 'not-found',
              licenses: [],
            };
          } else {
            return {
              kind: 'group',
              name: participant.name,
              status: 'not-found',
            };
          }
        });
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  startSearch = (): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state = searching(this.state);
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  cancelSearch = (): void => {
    switch (this.state.kind) {
      case 'searching': {
        const { kind, ...baseState } = this.state;
        this.state = { ...baseState, kind: 'ready' };
        break;
      }
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  startReview = (reviewEntry: ReviewEntry): void => {
    switch (this.state.kind) {
      case 'ready':
        this.state = reviewing(this.state, reviewEntry);
        break;
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  // Where the current review was opened from; before any review it is Confirm.
  @computed
  get reviewEntry(): ReviewEntry {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return 'confirm';
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return this.state.reviewEntry;
      default:
        return assertNever(this.state);
    }
  }

  @action
  cancelReview = (): void => {
    switch (this.state.kind) {
      case 'reviewing':
      case 'refreshing':
      case 'publishing': {
        const { kind, ...baseState } = this.state;
        this.state = { ...baseState, kind: 'ready' };
        break;
      }
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'searching':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  startRefresh = (): void => {
    switch (this.state.kind) {
      case 'reviewing':
        this.state = refreshing(this.state);
        break;
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'searching':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  completeRefresh = (): void => {
    switch (this.state.kind) {
      case 'refreshing':
      case 'publishing': {
        const { kind, ...baseState } = this.state;
        this.state = { ...baseState, kind: 'reviewing' };
        break;
      }
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  submitInvitation = (): void => {
    switch (this.state.kind) {
      case 'reviewing': {
        // Capture values before state transition
        const scheduleEnabled = this.state.scheduleEnabled;
        const scheduleModified = this.state.scheduleModified;
        const scheduleTimezone = this.state.scheduleTimezone;
        const learningCollectionId = this.state.learningCollectionResource.payload.id;
        const programId = this.state.programId;

        this.state = submitting(this.state);

        const userGroupParam: Maybe<{ id: number } | { name: string }> =
          this.state.userGroup.kind === 'selected'
            ? just({ id: this.state.userGroup.group.id })
            : this.state.userGroup.kind === 'new'
              ? just({ name: this.state.userGroup.name })
              : nothing();

        const participantsData: Record<string, unknown> = {
          learning_collection_id: learningCollectionId,
          user_ids: this.verifiedUserIds,
          group_ids: this.verifiedGroupIds,
          schedule_enabled: scheduleEnabled,
        };

        // Add program_id if sharing at the program level (direct enrollment)
        programId.do((id) => {
          participantsData.program_id = id;
        });

        // The server schedules each email no earlier than that person's license
        // start, at 9:00 AM in this zone when the admin sends now, so the zone
        // goes with every email invite, not only a chosen time (DVLP-8068).
        if (scheduleEnabled) {
          participantsData.timezone = scheduleTimezone;
        }

        // Only send scheduled_at if user explicitly modified the schedule
        // This avoids validation errors when the default "now" time has passed
        if (scheduleEnabled && scheduleModified) {
          this.mergedScheduleDateTime.do((mergedDateTime) => {
            // Send the wall-clock time the admin typed plus the zone they
            // picked, and let the server convert. toISOString() would stamp a
            // `Z`, which makes the server's TimeZone[tz].parse ignore
            // `timezone` and silently store the admin's own zone (DVLP-7797).
            participantsData.scheduled_at = toWallClockString(mergedDateTime);
            participantsData.timezone = scheduleTimezone;
          });
        }

        userGroupParam.do((param) => {
          participantsData.user_group = param;
        });

        const payload = { participants: participantsData };

        this.submitInvitationTask(payload).fork(
          action((err: ShareLearningCollectionError) => {
            console.error('Failed to submit invitation:', err);
            this.state = error('An error occurred, please refresh the page and try again');
          }),
          action((response: ShareLearningCollectionResponse) => {
            if (this.state.kind === 'submitting') {
              this.state = confirming(this.state, response);
            }
          }),
        );
        break;
      }
      case 'waiting':
      case 'loading':
      case 'ready':
      case 'searching':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  reset = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'confirming': {
        const { learningCollectionResource, shareBy, shareType, programId } = this.state;
        this.state = ready(learningCollectionResource, shareBy, shareType, programId);
        break;
      }
      case 'waiting':
      case 'loading':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateUnlicensedAccess = (option: UnlicensedAccessOption): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.unlicensedAccess = option;
        this.state.advancedSettingsModified = true;
        // Don't clear selectedLicenseId - keep its value in state
        // It will only be used/sent when option === 'allocate'
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  @action
  updateSelectedLicense = (licenseId: number): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
        this.state.selectedLicenseId = just(licenseId);
        this.state.advancedSettingsModified = true;
        break;
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  saveAdvancedSettings = (): Task<ShareLearningCollectionError, void> => {
    switch (this.state.kind) {
      case 'ready': {
        // Capture current state reference before async operation
        const currentState = this.state;

        const learningCollectionData: Record<string, unknown> = {
          unlicensed_access: this.state.unlicensedAccess,
        };

        if (this.state.unlicensedAccess === 'allocate') {
          this.state.selectedLicenseId.do((licenseId) => {
            learningCollectionData.license_id = licenseId;
          });
        }

        const payload = {
          learning_collection: learningCollectionData,
        };

        return this.updateLearningCollectionTask(payload).map(
          action((learningCollectionResource: LearningCollectionResource) => {
            // Only update if we're still in the same state instance
            // This prevents updates if user navigated away or state changed
            if (this.state === currentState) {
              this.state.learningCollectionResource = learningCollectionResource;
              this.state.advancedSettingsModified = false;
            }
          }),
        );
      }
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'waiting':
      case 'loading':
      case 'submitting':
      case 'confirming':
      case 'error':
        return Task.fail<ShareLearningCollectionError, void>(
          error('Cannot save advanced settings in the current state.'),
        );
      default:
        assertNever(this.state);
    }
  };

  @computed
  get learningCollection(): Maybe<LearningCollection> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return just(this.state.learningCollectionResource.payload);
      default:
        assertNever(this.state);
    }
  }

  @computed
  get learningCollectionResource(): Maybe<LearningCollectionResource> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return just(this.state.learningCollectionResource);
      default:
        assertNever(this.state);
    }
  }

  @computed
  get participants(): Participants {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return [];
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return this.state.participants;
      default:
        assertNever(this.state);
    }
  }

  @computed
  get totalParticipantCount(): number {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return 0;
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        // Collect all unique user IDs to avoid double-counting
        // users who are both directly invited and in groups
        const uniqueUserIds = new Set<number>();

        this.state.participants.forEach((participant) => {
          // Only count verified participants
          if (participant.status !== 'verified') {
            return;
          }

          if (participant.kind === 'user') {
            uniqueUserIds.add(participant.id);
          } else if (participant.kind === 'group' && 'members' in participant) {
            participant.members.forEach((member) => {
              uniqueUserIds.add(member.id);
            });
          }
        });

        return uniqueUserIds.size;
      default:
        assertNever(this.state);
    }
  }

  @computed
  get verifiedUserParticipants(): VerifiedUserParticipant[] {
    return this.participants.filter(
      (p): p is VerifiedUserParticipant => p.kind === 'user' && p.status === 'verified',
    );
  }

  @computed
  get verifiedGroupParticipants(): GroupParticipant[] {
    return this.participants.filter(
      (p): p is GroupParticipant => p.kind === 'group' && p.status === 'verified',
    );
  }

  @computed
  get notFoundParticipants(): Participant[] {
    return this.participants.filter((p) => p.status === 'not-found');
  }

  // Exactly the condition under which submitInvitation would post two empty
  // lists, which the server accepts and reports as a successful invitation.
  @computed
  get hasInvitableParticipants(): boolean {
    return this.verifiedUserIds.length > 0 || this.verifiedGroupIds.length > 0;
  }

  @computed
  get verifiedUserIds(): number[] {
    return this.verifiedUserParticipants.map((p) => p.id);
  }

  @computed
  get verifiedGroupIds(): number[] {
    return this.verifiedGroupParticipants.map((p) => p.id);
  }

  // Not part of the invite - submitInvitation sends verifiedUserIds - but the
  // license alert should still account for them.
  @computed
  get unlicensedUserIds(): number[] {
    return this.participants
      .filter((p): p is UnlicensedUserParticipant => p.kind === 'user' && p.status === 'unlicensed')
      .map((p) => p.id);
  }

  @computed
  get notFoundEmails(): string[] {
    return this.participants
      .filter((p) => p.kind === 'user' && p.status === 'not-found')
      .map((p) => p.email);
  }

  @computed
  get existingEmails(): Set<string> {
    return new Set(
      this.participants.filter((p) => p.kind === 'user').map((p) => p.email.toLowerCase()),
    );
  }

  @computed
  get existingGroupNames(): Set<string> {
    return new Set(
      this.participants.filter((p) => p.kind === 'group').map((p) => p.name.toLowerCase()),
    );
  }

  // The invitation toggle alone decides this. parse_scheduled_datetime returns
  // nil when schedule_enabled is false, and send_email = scheduled_datetime.present?,
  // so no email is sent at all without it. With the toggle on but no time chosen
  // the server falls back to fifteen minutes from now, so an email still goes
  // out - which is why this must not also require scheduleModified.
  @computed
  get willSendEmail(): boolean {
    return this.scheduleEnabled;
  }

  @computed
  get scheduleEnabled(): boolean {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return false;
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return this.state.scheduleEnabled;
    }
  }

  @computed
  get scheduleTime(): Maybe<Date> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return just(this.state.scheduleTime);
    }
  }

  @computed
  get scheduleDate(): Maybe<Date> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        return just(this.state.scheduleDate);
    }
  }

  @computed
  get mergedScheduleDateTime(): Maybe<Date> {
    switch (this.state.kind) {
      case 'waiting':
      case 'loading':
      case 'error':
        return nothing();
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        // Merge scheduleDate and scheduleTime into a single datetime
        const mergedDateTime = new Date(this.state.scheduleDate);
        mergedDateTime.setHours(this.state.scheduleTime.getHours());
        mergedDateTime.setMinutes(this.state.scheduleTime.getMinutes());
        mergedDateTime.setSeconds(this.state.scheduleTime.getSeconds());
        mergedDateTime.setMilliseconds(this.state.scheduleTime.getMilliseconds());
        return just(mergedDateTime);
      default:
        assertNever(this.state);
    }
  }

  @computed
  get successfulParticipantCount(): number {
    switch (this.state.kind) {
      case 'confirming':
        return this.state.response.successfulParticipants.length;
      case 'waiting':
      case 'loading':
      case 'error':
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
        return 0;
      default:
        assertNever(this.state);
    }
  }

  // Invited people whose access waits for a license start (DVLP-8068).
  @computed
  get deferredParticipants(): ReadonlyArray<SuccessfulParticipant> {
    switch (this.state.kind) {
      case 'confirming':
        return this.state.response.successfulParticipants.filter((participant) =>
          participant.licenseStartOn.isJust(),
        );
      case 'waiting':
      case 'loading':
      case 'error':
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get failedParticipantCount(): number {
    switch (this.state.kind) {
      case 'confirming':
        return this.state.response.failedParticipants.length;
      case 'waiting':
      case 'loading':
      case 'error':
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
        return 0;
      default:
        assertNever(this.state);
    }
  }

  @computed
  get failedParticipants(): ReadonlyArray<FailedParticipant> {
    switch (this.state.kind) {
      case 'confirming':
        return this.state.response.failedParticipants;
      case 'waiting':
      case 'loading':
      case 'error':
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
        return [];
      default:
        assertNever(this.state);
    }
  }

  @computed
  get availableLicenses(): ReadonlyArray<LicenseOption> {
    return this.learningCollection.cata({
      Just: (lc) => lc.availableLicenses,
      Nothing: () => [],
    });
  }

  @computed
  get shareUrlState(): ShareUrlState {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming': {
        const programShareUrlState = this.state.programShareUrlState;
        const collectionHref = this.learningCollection
          .andThen((lc) => lc.shareUrl)
          .andThen((shareUrlResource) =>
            findLinkBy({ rel: 'shared-url-short-link', type: 'text/html' }, shareUrlResource.links),
          )
          .map((link) => link.href);

        switch (programShareUrlState.kind) {
          case 'idle':
            return collectionHref.cata({
              Just: shareUrlReady,
              Nothing: noShareUrl,
            });
          case 'loading':
            return collectionHref.cata({
              Just: shareUrlLoading,
              Nothing: noShareUrl,
            });
          case 'loaded':
            return findLinkBy(
              { rel: 'shared-url-short-link', type: 'text/html' },
              programShareUrlState.shareUrlResource.links,
            ).cata({
              Just: (link) => shareUrlReady(link.href),
              Nothing: () =>
                collectionHref.cata({
                  Just: shareUrlReady,
                  Nothing: noShareUrl,
                }),
            });
          case 'error':
            return collectionHref.cata({
              Just: shareUrlError,
              Nothing: noShareUrl,
            });
        }
      }
      case 'waiting':
      case 'loading':
      case 'error':
        return noShareUrl();
    }
  }

  private isCurrentProgramShareUrlRequest = (programFamilyId: number): boolean => {
    return (
      isActiveShareState(this.state) &&
      this.state.programShareUrlState.kind === 'loading' &&
      this.state.programShareUrlState.programFamilyId === programFamilyId
    );
  };

  @action
  private setProgramShareUrlError = (programFamilyId: number): void => {
    if (isActiveShareState(this.state)) {
      this.state.programShareUrlState = { kind: 'error', programFamilyId };
    }
  };

  @action
  private setProgramShareUrlLoaded = (shareUrlResource: ShareUrlResource): void => {
    if (isActiveShareState(this.state)) {
      this.state.programShareUrlState = { kind: 'loaded', shareUrlResource };
    }
  };

  @action
  fetchProgramShareUrl = (programFamilyId: number): void => {
    if (!isActiveShareState(this.state)) return;
    if (this.state.kind === 'submitting' || this.state.kind === 'confirming') return;

    this.state.programShareUrlState = { kind: 'loading', programFamilyId };

    this.fetchProgramShareUrlTask(programFamilyId).fork(
      (err: ShareLearningCollectionError) => {
        console.error(
          `Failed to fetch program share URL for program family ${programFamilyId}:`,
          err,
        );
        if (this.isCurrentProgramShareUrlRequest(programFamilyId)) {
          this.setProgramShareUrlError(programFamilyId);
        }
      },
      (shareUrlResource: ShareUrlResource) => {
        if (this.isCurrentProgramShareUrlRequest(programFamilyId)) {
          this.setProgramShareUrlLoaded(shareUrlResource);
        }
      },
    );
  };

  @action
  clearProgramShareUrl = (): void => {
    switch (this.state.kind) {
      case 'ready':
      case 'searching':
      case 'reviewing':
      case 'refreshing':
      case 'publishing':
      case 'submitting':
      case 'confirming':
        this.state.programShareUrlState = { kind: 'idle' };
        break;
      case 'waiting':
      case 'loading':
      case 'error':
        break;
      default:
        assertNever(this.state);
    }
  };

  private addParticipantsParams =
    (emails: string[], groupNames: string[], learningCollectionId: number) =>
    (link: Link): Task<ShareLearningCollectionError, Link> => {
      return toUrlT(link.href).map((u) => {
        // Build query params manually for array parameters
        const params = new URLSearchParams();

        emails.forEach((email) => {
          params.append('emails[]', email);
        });

        groupNames.forEach((groupName) => {
          params.append('group_names[]', groupName);
        });

        params.append('learning_collection_id', learningCollectionId.toString());

        const queryString = params.toString();
        const separator = u.href.includes('?') ? '&' : '?';
        const newHref = queryString ? `${u.href}${separator}${queryString}` : u.href;

        return { ...link, href: newHref };
      });
    };

  private loadLearningCollectionTask = (
    learningCollectionId: number,
  ): Task<ShareLearningCollectionError, LearningCollectionResource> => {
    return Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-learning-collection'))
      .andThen(this.addLearningCollectionIdParam(learningCollectionId))
      .andThen(callApi(learningCollectionResourceDecoder, {}));
  };

  private togglePublishLearningCollectionTask = (
    published: boolean,
  ): Task<ShareLearningCollectionError, {}> => {
    return this.learningCollectionResource.cata({
      Just: (resource) =>
        Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(resource.links)
          .andThen(findLinkT('publish'))
          .andThen(callApi(succeed({}), { published })),
      Nothing: () =>
        Task.fail<ShareLearningCollectionError, {}>(
          error('Learning collection resource not loaded.'),
        ),
    });
  };

  private addLearningCollectionIdParam =
    (learningCollectionId: number) =>
    (link: Link): Task<ShareLearningCollectionError, Link> => {
      return toUrlT(link.href).map((u) => {
        const newHref = u.href.replace(':id', learningCollectionId.toString());
        return { ...link, href: newHref };
      });
    };

  private verifyParticipantsTask = (
    emails: string[],
    groupNames: string[],
    learningCollectionId: number,
  ): Task<ShareLearningCollectionError, Participant[]> => {
    return Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-participants-directory'))
      .andThen(this.addParticipantsParams(emails, groupNames, learningCollectionId))
      .andThen(callApi(participantsResponseDecoder, {}));
  };

  private submitInvitationTask = (
    payload: Record<string, unknown>,
  ): Task<ShareLearningCollectionError, ShareLearningCollectionResponse> => {
    return Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(currentUserStore.links)
      .andThen(findLinkT('adminstudio-share-learning-collections-share'))
      .andThen(callApi(shareLearningCollectionResponseDecoder, payload));
  };

  private updateLearningCollectionTask = (
    payload: Record<string, unknown>,
  ): Task<ShareLearningCollectionError, LearningCollectionResource> => {
    return this.learningCollectionResource.cata({
      Just: (resource) =>
        Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(resource.links)
          .andThen(findLinkT('update'))
          .andThen(callApi(learningCollectionResourceDecoder, payload)),
      Nothing: () =>
        Task.fail<ShareLearningCollectionError, LearningCollectionResource>(
          error('Learning collection resource not loaded.'),
        ),
    });
  };

  private fetchProgramShareUrlTask = (
    programFamilyId: number,
  ): Task<ShareLearningCollectionError, ShareUrlResource> => {
    return this.learningCollectionResource.cata({
      Just: (resource) => {
        const useCaseId = resource.payload.id;

        // Get the template link from currentUserStore and expand it
        return Task.succeed<ShareLearningCollectionError, ReadonlyArray<Link>>(
          currentUserStore.links,
        )
          .andThen(findLinkT('adminstudio-program-family-share-urls'))
          .map((link) => ({
            ...link,
            href: link.href
              .replace('{learning_collection_id}', useCaseId.toString())
              .replace('{program_family_id}', programFamilyId.toString()),
          }))
          .andThen(callApi(shareUrlResourceDecoder, {}));
      },
      Nothing: () =>
        Task.fail<ShareLearningCollectionError, ShareUrlResource>(
          error('Learning collection resource not loaded.'),
        ),
    });
  };

  private parseParticipantInput = (input: string): UnverifiedParticipant[] => {
    if (!input.trim()) return [];

    // Split by newlines, commas, or semicolons and filter empty strings
    const entries = input
      .split(/[\n,;]+/)
      .map((entry) => entry.trim())
      .filter((entry) => entry.length > 0);

    return entries.map((entry) => {
      // Simple email detection - if it contains @ it's likely an email
      if (entry.includes('@')) {
        return {
          kind: 'user' as const,
          email: entry,
          status: 'pending' as const,
        };
      } else {
        // Treat as group name
        return {
          kind: 'group' as const,
          name: entry,
          status: 'pending' as const,
        };
      }
    });
  };

  private participantEquals = (a: Participant, b: Participant): boolean => {
    if (a.kind !== b.kind) return false;

    if (a.kind === 'user' && b.kind === 'user') {
      return a.email === b.email;
    }

    if (a.kind === 'group' && b.kind === 'group') {
      return a.name === b.name;
    }

    return false;
  };
}

const shareStore = new ShareStore();

export { shareStore };
